/**
 * GlobalSearch (C-5b) — cross-module record search. Finds a record by number
 * or title across every quality/RA module (deviation, CAPA, OOS, risk, change
 * control, complaint, recall, registration, variation, commitment, validation,
 * SCAR, product, document) via GET /api/v1/search, grouped by module, each
 * linking through to its detail. Route: /search
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageHero } from '@edms/design-system';
import styles from '../OcrSearch/OcrSearch.module.css';
import { apiFetch } from '../../api/client';

interface SearchHit { entityType: string; entityId: string; number: string | null; title: string | null; status: string | null; path: string }
interface SearchResp { query: string; items: SearchHit[]; total: number; byType: Record<string, number> }

const TYPE_LABEL: Record<string, string> = {
  DEVIATION: 'Deviations', CAPA: 'CAPAs', OOS: 'Out-of-Spec', RISK: 'Risks',
  CHANGE_CONTROL: 'Change Controls', COMPLAINT: 'Complaints', RECALL: 'Recalls',
  REGISTRATION: 'Registrations', VARIATION: 'Variations', COMMITMENT: 'Commitments',
  VALIDATION: 'Validation', SCAR: 'Supplier SCARs', PRODUCT: 'Products', DOCUMENT: 'Documents',
};

export function GlobalSearch() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');

  const q = useQuery<SearchResp>({
    queryKey: ['global-search', query],
    queryFn: ({ signal }) => apiFetch<SearchResp>(`/api/v1/search?q=${encodeURIComponent(query)}`, { signal }),
    enabled: query.trim().length >= 2,
    staleTime: 20_000,
  });

  function submit(e: React.FormEvent) { e.preventDefault(); setQuery(input.trim()); }

  const items = q.data?.items ?? [];
  // Group by entity type, preserving the label order above.
  const groups = Object.keys(TYPE_LABEL)
    .map((t) => ({ type: t, label: TYPE_LABEL[t], hits: items.filter((i) => i.entityType === t) }))
    .filter((g) => g.hits.length > 0);

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Search"
        title="Global Search"
        subtitle="Find any record across the platform — deviations, CAPAs, OOS, risks, change controls, complaints, recalls, registrations, variations, commitments, validation, SCARs, products and documents — by number or title."
      />

      <form className={styles.searchBar} onSubmit={submit} role="search">
        <input
          type="search"
          className={styles.searchInput}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search all records… e.g. DEV-2026, GlassPharma, Sitagliptin"
          aria-label="Global search query"
        />
        <button type="submit" className={styles.searchBtn} disabled={input.trim().length < 2}>Search</button>
      </form>

      {query.trim().length >= 2 && (
        <div className={styles.meta}>
          {q.isLoading ? 'Searching…'
            : q.isError ? 'Search failed — try again.'
            : `${q.data?.total ?? 0} result${(q.data?.total ?? 0) === 1 ? '' : 's'} across ${groups.length} module${groups.length === 1 ? '' : 's'} for “${query}”`}
        </div>
      )}

      {query.trim().length >= 2 && !q.isLoading && !q.isError && items.length === 0 && (
        <div className={styles.empty}>No records match “{query}”.</div>
      )}

      <div className={styles.results}>
        {groups.map((g) => (
          <article key={g.type} className={styles.card}>
            <div className={styles.cardHead} style={{ cursor: 'default' }}>
              <span className={styles.docTitle}>{g.label}</span>
              <span className={styles.metaTag}>{g.hits.length}</span>
            </div>
            <ul className={styles.pages}>
              {g.hits.map((h) => (
                <li key={`${h.entityType}:${h.entityId}`} className={styles.pageRow}>
                  <button type="button" className={styles.snippet} style={{ textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={() => navigate({ to: `${h.path}/${h.entityId}` as never }).catch(() => navigate({ to: h.path as never }))}>
                    {h.number && <strong style={{ color: 'var(--color-brand-700, #155e9c)' }}>{h.number}</strong>}
                    {h.number ? ' · ' : ''}{h.title || '(untitled)'}
                  </button>
                  {h.status && <span className={styles.conf}>{h.status}</span>}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </main>
  );
}
